import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, FileText, Loader2 } from "lucide-react";
import TurbineHero from "./TurbineHero";
import { HERO } from "../constants/testIds";
import { useContent } from "../hooks/useContent";

const STAGES = [
    { label: "POLISHED", desc: "Finition titane" },
    { label: "GLASS", desc: "Vue translucide" },
    { label: "INTERNALS", desc: "Composants moteur" },
    { label: "BLUEPRINT", desc: "Plan technique" },
    { label: "ASSEMBLED", desc: "Vue finale" },
];

const colorMap = {
    emerald: "bg-emerald-500",
    blue: "bg-[#0a84ff]",
    neutral: "bg-neutral-400",
};

const Hero = ({ onQuoteClick }) => {
    const sectionRef = useRef(null);
    const [stage, setStage] = useState(0);
    const { data, isLoading } = useContent("hero");
    const content = data?.data || {};

    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start start", "end start"],
    });

    useEffect(() => {
        const unsub = scrollYProgress.on("change", (v) => {
            const next = Math.min(4, Math.max(0, v * 4.4));
            setStage(next);
        });
        return () => unsub();
    }, [scrollYProgress]);

    const headlineY = useTransform(scrollYProgress, [0, 1], [0, -40]);
    const productY = useTransform(scrollYProgress, [0, 1], [0, -60]);
    const productScale = useTransform(scrollYProgress, [0, 1], [1, 1.06]);

    const title = content.title || "Votre fournisseur de matériel dentaire professionnel au Maroc.";
    const subtitle =
        content.subtitle ||
        "Équipements, consommables et fournitures dentaires de précision pour cabinets et laboratoires — turbines, implantologie, endodontie. Livraison nationale 24h-72h.";
    const visualImage = content.visual_image || "";
    const visualAlt = content.visual_alt || "Turbine dentaire haute performance";

    return (
        <section
            id="top"
            ref={sectionRef}
            data-testid={HERO.root}
            className="relative pt-12 pb-10 overflow-hidden bg-[#FBFBFD]"
        >
            {/* Background layers */}
            <div className="absolute inset-0 dp-grid-lines opacity-60" />
            <div
                className="absolute inset-0"
                style={{
                    background:
                        "radial-gradient(60% 50% at 70% 30%, rgba(0,229,255,0.08), transparent 70%), radial-gradient(40% 40% at 20% 80%, rgba(10,132,255,0.06), transparent 70%)",
                }}
            />

            <div className="relative max-w-[1320px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
                {/* Left text */}
                <motion.div
                    style={{ y: headlineY }}
                    className="lg:col-span-6 relative z-10"
                >
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="dp-eyebrow mb-6 flex items-center gap-2"
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-[#0a84ff]" />
                        {content.eyebrow || "Dentiprime · Fournisseur dentaire au Maroc"}
                    </motion.div>

                    <motion.h1
                        data-testid={HERO.title}
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.05 }}
                        className="font-display text-[44px] leading-[1.02] sm:text-5xl md:text-6xl lg:text-[68px] font-light tracking-[-0.025em] text-neutral-900"
                    >
                        {title.split(content.title_highlight || "matériel dentaire")[0]}
                        <span className="italic font-extralight text-neutral-500">
                            {content.title_highlight || "matériel dentaire"}
                        </span>
                        {title.split(content.title_highlight || "matériel dentaire")[1] || ""}
                        <span className="relative">
                            <svg
                                className="absolute -bottom-2 left-0 w-full"
                                height="6"
                                viewBox="0 0 200 6"
                                preserveAspectRatio="none"
                            >
                                <path
                                    d="M2 4 Q 50 1 100 3 T 198 3"
                                    stroke="#00e5ff"
                                    strokeWidth="2"
                                    fill="none"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </span>
                    </motion.h1>

                    <motion.p
                        data-testid={HERO.sub}
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.18 }}
                        className="mt-7 text-[17px] md:text-[18px] leading-relaxed text-neutral-500 max-w-[540px]"
                    >
                        {subtitle}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.3 }}
                        className="mt-9 flex flex-wrap items-center gap-3"
                    >
                        <a
                            href="#produits"
                            data-testid={HERO.ctaCatalog}
                            className="dp-btn dp-btn-primary"
                        >
                            {content.cta_catalog || "Découvrir le catalogue"}
                            <ArrowRight className="w-4 h-4" />
                        </a>
                        <button
                            data-testid={HERO.ctaQuote}
                            onClick={onQuoteClick}
                            className="dp-btn dp-btn-ghost"
                        >
                            <FileText className="w-4 h-4" />
                            {content.cta_quote || "Demander un devis"}
                        </button>
                    </motion.div>

                    {/* Trust line */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 0.6 }}
                        className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-[12.5px] text-neutral-500"
                    >
                        {(content.trust_lines || [
                            { label: "Stock vérifié quotidiennement", color: "emerald" },
                            { label: "Livraison nationale {{flag}} 24–72 h", color: "blue" },
                            { label: "Support technique francophone", color: "neutral" },
                        ]).map((line) => {
                            const flagImg = (
                                <img
                                    src="/images/flag-morocco.svg"
                                    alt="Maroc"
                                    className="w-4 h-3 object-cover rounded-sm inline-block"
                                />
                            );
                            const parts = line.label.split("{{flag}}");
                            return (
                                <span key={line.label} className="inline-flex items-center gap-2">
                                    <span className={`w-1.5 h-1.5 rounded-full ${colorMap[line.color] || "bg-neutral-400"}`} />
                                    <span className="inline-flex items-center gap-1.5">
                                        {parts.map((part, i) => (
                                            <React.Fragment key={i}>
                                                {part}
                                                {i < parts.length - 1 && flagImg}
                                            </React.Fragment>
                                        ))}
                                    </span>
                                </span>
                            );
                        })}
                    </motion.div>
                </motion.div>

                {/* Right hero product */}
                <motion.div
                    style={{ y: productY, scale: productScale }}
                    className="lg:col-span-6 relative z-0"
                >
                    <div className="relative aspect-square max-w-[640px] mx-auto">
                        {!visualImage && (
                            <>
                                {/* Concentric blueprint rings */}
                                <svg
                                    viewBox="0 0 600 600"
                                    className="absolute inset-0 w-full h-full dp-spin-rev opacity-60"
                                >
                                    <g
                                        stroke="#0a84ff"
                                        strokeWidth="0.6"
                                        fill="none"
                                        strokeDasharray="3 6"
                                    >
                                        <circle cx="300" cy="300" r="280" />
                                        <circle cx="300" cy="300" r="240" strokeDasharray="2 10" />
                                        <circle cx="300" cy="300" r="200" />
                                    </g>
                                    <g fill="#0a84ff" fontSize="9" fontFamily="JetBrains Mono">
                                        <text x="295" y="22">N</text>
                                        <text x="580" y="305">E</text>
                                        <text x="295" y="592">S</text>
                                        <text x="6" y="305">W</text>
                                    </g>
                                </svg>

                                <div className="absolute inset-0 dp-float">
                                    <TurbineHero stage={stage} />
                                </div>
                            </>
                        )}

                        {visualImage && (
                            <div className="absolute inset-0 flex items-center justify-center dp-float">
                                <img
                                    src={visualImage}
                                    alt={visualAlt}
                                    className="max-w-full max-h-full object-contain drop-shadow-2xl"
                                />
                            </div>
                        )}

                        {/* Floating spec chips */}
                        {(content.floating_chips || [
                            { label: "Tête", value: "45° · Push button", position: "top-right" },
                            { label: "Rotor", value: "≤ 420 000 tr/min", position: "bottom-left" },
                            { label: "Spray", value: "4 jets · Eau / Air", position: "bottom-right" },
                        ]).map((chip, i) => {
                            const posClasses = {
                                "top-right": "top-6 right-2",
                                "bottom-left": "bottom-12 left-0",
                                "bottom-right": "bottom-2 right-8",
                            };
                            return (
                                <motion.div
                                    key={chip.label}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.6 + i * 0.2, duration: 0.7 }}
                                    className={`absolute ${posClasses[chip.position] || "top-6 right-2"} dp-glass rounded-2xl px-4 py-3 text-left`}
                                >
                                    <div className="dp-eyebrow !text-[10px]">{chip.label}</div>
                                    <div className="font-display text-sm">{chip.value}</div>
                                </motion.div>
                            );
                        })}
                    </div>

                    {/* Stage indicator */}
                    {!visualImage && (
                        <>
                            <div className="mt-6 mx-auto flex items-center justify-center gap-1.5">
                                {STAGES.map((s, i) => {
                                    const active = Math.round(stage) === i;
                                    return (
                                        <div key={s.label} className="flex items-center gap-1.5">
                                            <div
                                                className={`h-[3px] rounded-full transition-all duration-500 ${
                                                    active ? "w-10 bg-[#0a84ff]" : "w-5 bg-neutral-300"
                                                }`}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="mt-2 text-center font-mono-tech text-[10px] tracking-[0.25em] text-neutral-500 uppercase">
                                {STAGES[Math.min(4, Math.round(stage))].label} ·{" "}
                                {STAGES[Math.min(4, Math.round(stage))].desc}
                            </div>
                        </>
                    )}
                </motion.div>
            </div>

        </section>
    );
};

export default Hero;
